import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Globe2,
  BarChart3,
  Settings,
  Rocket,
  Smartphone,
  Menu,
  ChevronLeft,
  PiIcon,
  BarChart4,
  DollarSign,
  BarChart2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";

const menu = [
  { name: "Dashboard", path: "/", icon: LayoutDashboard },
  { name: "Apps/Websites", path: "/apps", icon: Globe2 },
  { name: "Web Analytics", path: "/web-analytics", icon: BarChart3 },
  { name: "App Analytics", path: "/app-analytics", icon: BarChart4 },
  { name: "Payment Info", path: "/payment-info", icon: DollarSign },
  { name: "Invoice", path: "/publisher-invoice", icon: BarChart2 },
  { name: "Settings", path: "/settings", icon: Settings },
];

export default function PublisherSidebar() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(true);

  const toggleSidebar = () => setIsOpen(!isOpen);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined' && window.innerWidth < 768) setIsOpen(false);
      else setIsOpen(true);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      {/* Optional backdrop for small screens */}
      {isOpen && window.innerWidth < 768 && (
        <div
          className="fixed inset-0 z-20 bg-black/30 md:hidden"
          onClick={toggleSidebar}
        />
      )}


      <motion.aside
        animate={{ width: isOpen ? 260 : 80 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`fixed md:relative top-0 left-0 z-30 h-screen bg-gradient-to-br from-white via-gray-50 to-gray-100 border-r border-gray-200 shadow-lg p-4 flex flex-col justify-between overflow-hidden transition-all duration-300`}
      >
        {/* Top Section */}
        <div>
          {/* Toggle Button */}
          <button
            onClick={toggleSidebar}
            className="mb-6 flex items-center justify-between w-full text-gray-600 hover:text-indigo-500 transition"
          >
            {isOpen ? (
              <ChevronLeft className="w-5 h-5 ml-auto" />
            ) : (
              <Menu className="w-5 h-5 mx-auto" />
            )}
          </button>

          {/* Brand Title */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="flex items-center gap-2 mb-8 px-2"
              >
                <Rocket className="w-6 h-6 text-pink-500 animate-bounce-slow drop-shadow" />
                <h1 className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-transparent bg-clip-text drop-shadow-sm animate-gradient-x whitespace-nowrap">
                  Adblast<span className="text-pink-600">X</span>
                </h1>
              </motion.div>
            )}
          </AnimatePresence>


          {/* Navigation Links */}
          <nav className="flex flex-col gap-3 relative">
            <AnimatePresence>
              {menu.map((item, index) => {
                const isActive = location.pathname === item.path;
                return (
                  <motion.div
                    key={item.name}
                    layout
                    className="relative"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: index * 0.02 }}
                  >
                    <Link
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-2 rounded-xl group transition-all duration-300 relative z-10 ${isActive
                        ? "text-white font-semibold bg-gradient-to-r from-indigo-500 to-purple-500 shadow-md"
                        : "text-gray-600 hover:bg-gray-200"
                        }`}
                    >
                      <div className="relative group">
                        <item.icon
                          className={`w-5 h-5 transition-transform duration-200 ${isActive ? "text-white" : "text-indigo-500"
                            } group-hover:scale-110`}
                        />
                        {/* Tooltip when collapsed */}
                        {!isOpen && (
                          <span className="absolute left-10 top-1/2 -translate-y-1/2 whitespace-nowrap bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 transition pointer-events-none z-50">
                            {item.name}
                          </span>
                        )}
                      </div>
                      <motion.span
                        initial={false}
                        animate={{ opacity: isOpen ? 1 : 0, width: isOpen ? "auto" : 0 }}
                        className="text-sm truncate origin-left"
                      >
                        {item.name}
                      </motion.span>
                    </Link>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </nav>
        </div>

        {/* Footer */}
        <motion.div
          animate={{ opacity: isOpen ? 1 : 0 }}
          className="text-xs text-gray-400 text-center mt-6"
        >
          © {new Date().getFullYear()} AdblastX
        </motion.div>
      </motion.aside>
    </>
  );
}
